import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getProduct, getShippingRates } from '../services/api';
import { useCart } from '../context/CartContext';
import './ProductDetail.css';

const ESTIMATE_STORAGE_KEY = 'anoraShippingEstimateAddress';
const PREFERRED_CARRIERS = ['UPS', 'USPS', 'FedEx'];

function summarizeCarrierRates(rates = []) {
  const preferredRates = rates.filter((rate) =>
    PREFERRED_CARRIERS.some((carrier) => rate.carrier?.toLowerCase().includes(carrier.toLowerCase()))
  );
  const source = preferredRates.length ? preferredRates : rates;

  const bestByCarrier = new Map();
  source.forEach((rate) => {
    const key = rate.carrier || 'Shipping';
    if (!bestByCarrier.has(key) || rate.rate < bestByCarrier.get(key).rate) {
      bestByCarrier.set(key, rate);
    }
  });

  return Array.from(bestByCarrier.values()).sort((a, b) => a.rate - b.rate);
}

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [estimateAddress, setEstimateAddress] = useState(() => {
    try {
      const saved = localStorage.getItem(ESTIMATE_STORAGE_KEY);
      return saved
        ? JSON.parse(saved)
        : { street: '', city: '', state: '', zip: '', country: 'US' };
    } catch {
      return { street: '', city: '', state: '', zip: '', country: 'US' };
    }
  });
  const [carrierRates, setCarrierRates] = useState([]);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingError, setShippingError] = useState('');
  const [shippingFetched, setShippingFetched] = useState(false);
  const [shippingStale, setShippingStale] = useState(false);
  const { addItem, addSavedItem, savedItems } = useCart();

  useEffect(() => {
    getProduct(id)
      .then(r => setProduct(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const handleAdd = () => {
    addItem(product, qty);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const isSaved = savedItems.some((item) => item.id === product?.id);

  const updateEstimateField = (key, value) => {
    setEstimateAddress((current) => ({ ...current, [key]: value }));
    setShippingStale(shippingFetched);
  };

  const fetchShippingEstimate = async () => {
    setShippingLoading(true);
    setShippingError('');
    setShippingStale(false);

    try {
      const normalizedAddress = {
        name: 'Shipping Estimate',
        street: estimateAddress.street.trim(),
        city: estimateAddress.city.trim(),
        state: estimateAddress.state.trim().toUpperCase(),
        zip: estimateAddress.zip.trim(),
        country: estimateAddress.country || 'US',
      };

      const { data } = await getShippingRates({
        toAddress: normalizedAddress,
        items: [{ id: product.id, qty }],
      });

      setCarrierRates(summarizeCarrierRates(data.rates));
      setShippingFetched(true);
      localStorage.setItem(ESTIMATE_STORAGE_KEY, JSON.stringify(normalizedAddress));
    } catch (err) {
      setShippingError(err.response?.data?.error || 'Could not fetch live carrier rates for this item yet.');
      setCarrierRates([]);
      setShippingFetched(false);
    } finally {
      setShippingLoading(false);
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;
  if (!product) return <div className="container" style={{ padding: '2rem' }}><p>Product not found.</p></div>;

  const images = product.images || [];
  const hasStock = product.quantity > 0;
  const stockLabel = hasStock
    ? product.quantity <= 3
      ? `Only ${product.quantity} left`
      : `${product.quantity} in stock`
    : 'Out of stock';
  const canEstimateShipping = Boolean(
    estimateAddress.street.trim() &&
    estimateAddress.city.trim() &&
    estimateAddress.state.trim() &&
    estimateAddress.zip.trim()
  );

  return (
    <div className="container" style={{ padding: '2rem 1.5rem' }}>
      <div className="detail-breadcrumbs">
        <Link to="/" className="back-link">← Back to listings</Link>
        {product.category && <span>{product.category}</span>}
      </div>

      <div className="product-detail">
        {/* Images */}
        <div className="detail-images">
          <div className="main-image">
            {images.length > 0 ? (
              <img src={images[selectedImage]} alt={product.title} />
            ) : (
              <div className="no-image-large">📦</div>
            )}
          </div>
          {images.length > 1 && (
            <div className="thumbnail-row">
              {images.map((img, i) => (
                <img
                  key={i}
                  src={img}
                  alt=""
                  className={`thumbnail ${i === selectedImage ? 'active' : ''}`}
                  onClick={() => setSelectedImage(i)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="detail-info">
          {product.category && <p className="detail-category">{product.category}</p>}
          <h1 className="detail-title">{product.title}</h1>

          <div className="detail-meta">
            <span className={`badge ${product.condition === 'New' ? 'badge-success' : 'badge-warning'}`}>
              {product.condition}
            </span>
            <span className={`stock-info ${hasStock ? '' : 'out'}`}>{stockLabel}</span>
            {product.on_sale && (
              <span className="badge badge-danger">{product.sale_percent}% off</span>
            )}
          </div>

          <div className="detail-price-wrap">
            <div className="detail-price">${parseFloat(product.price).toFixed(2)}</div>
            {product.on_sale && (
              <div className="detail-price-compare">
                Was ${parseFloat(product.base_price).toFixed(2)} — save ${parseFloat(product.savings_amount).toFixed(2)}
              </div>
            )}
          </div>

          <div className="detail-highlights">
            <div className="detail-highlight">
              <strong>Ships fast</strong>
              <span>Live UPS, FedEx, and USPS rates in your cart before payment.</span>
            </div>
            <div className="detail-highlight">
              <strong>Protected payment</strong>
              <span>Complete your order with Stripe or PayPal.</span>
            </div>
            <div className="detail-highlight">
              <strong>Order tracking</strong>
              <span>Track your package as soon as the label is created.</span>
            </div>
            <div className="detail-highlight">
              <strong>30-day free returns</strong>
              <span>Eligible orders are covered by our 30-day free return policy.</span>
            </div>
          </div>

          {product.description && (
            <div className="detail-description">
              <h3>Description</h3>
              <p>{product.description}</p>
            </div>
          )}

          <div className="shipping-estimator card">
            <div className="shipping-estimator__header">
              <div>
                <span className="detail-category">Shipping estimate</span>
                <h3>See live UPS, USPS, and FedEx pricing for this item.</h3>
              </div>
              <span className="shipping-estimator__qty">Qty {qty}</span>
            </div>

            <div className="shipping-estimator__grid">
              <div className="form-group">
                <label className="form-label">Street</label>
                <input
                  className="form-control"
                  value={estimateAddress.street}
                  onChange={(e) => updateEstimateField('street', e.target.value)}
                  placeholder="123 Main St"
                />
              </div>
              <div className="form-group">
                <label className="form-label">City</label>
                <input
                  className="form-control"
                  value={estimateAddress.city}
                  onChange={(e) => updateEstimateField('city', e.target.value)}
                  placeholder="Miami"
                />
              </div>
              <div className="form-group">
                <label className="form-label">State</label>
                <input
                  className="form-control"
                  value={estimateAddress.state}
                  onChange={(e) => updateEstimateField('state', e.target.value.toUpperCase())}
                  placeholder="FL"
                  maxLength={2}
                />
              </div>
              <div className="form-group">
                <label className="form-label">ZIP</label>
                <input
                  className="form-control"
                  value={estimateAddress.zip}
                  onChange={(e) => updateEstimateField('zip', e.target.value)}
                  placeholder="33101"
                />
              </div>
            </div>

            <div className="shipping-estimator__actions">
              <button
                className="btn btn-secondary"
                onClick={fetchShippingEstimate}
                disabled={!canEstimateShipping || shippingLoading}
              >
                {shippingLoading ? 'Checking live rates...' : 'Get live shipping costs'}
              </button>
              <p>Rates are calculated from your shipping address using this item’s real weight and dimensions.</p>
            </div>

            {shippingStale && (
              <div className="alert alert-info shipping-estimator__status">
                Quantity or address changed. Refresh the estimate to see the latest carrier pricing.
              </div>
            )}

            {shippingError && (
              <div className="alert alert-error shipping-estimator__status">
                {shippingError}
              </div>
            )}

            {shippingFetched && carrierRates.length > 0 && (
              <div className="carrier-rate-grid">
                {carrierRates.map((rate) => (
                  <div key={`${rate.carrier}-${rate.service}`} className="carrier-rate-card">
                    <strong>{rate.carrier}</strong>
                    <span>{rate.service}</span>
                    <div className="carrier-rate-price">${rate.rate.toFixed(2)}</div>
                    <small>{rate.days ? `${rate.days} day${rate.days > 1 ? 's' : ''}` : 'ETA varies'}</small>
                  </div>
                ))}
              </div>
            )}
          </div>

          {hasStock && (
            <div className="detail-actions">
              <div className="qty-selector">
                <button onClick={() => { setQty(q => Math.max(1, q - 1)); setShippingStale(shippingFetched); }}>−</button>
                <span>{qty}</span>
                <button onClick={() => { setQty(q => Math.min(product.quantity, q + 1)); setShippingStale(shippingFetched); }}>+</button>
              </div>
              <button className="btn btn-primary btn-lg" onClick={handleAdd} style={{ flex: 1 }}>
                {added ? '✓ Added!' : '🛍️ Add to Cart'}
              </button>
              <button className="btn btn-secondary btn-lg detail-save-btn" onClick={() => addSavedItem(product)}>
                {isSaved ? 'Saved' : 'Save for later'}
              </button>
            </div>
          )}

          {!hasStock && (
            <div className="detail-out-of-stock card">
              <strong>This item is currently unavailable.</strong>
              <p>Keep browsing the catalog or check back later for a restock.</p>
            </div>
          )}

          <div className="shipping-teaser">
            <div className="shipping-row">🚚 <span>Fast shipping available via UPS, FedEx & USPS</span></div>
            <div className="shipping-row">🔒 <span>Secure checkout with Stripe or PayPal</span></div>
            <div className="shipping-row">📦 <span>Real-time tracking included</span></div>
            <div className="shipping-row">↩️ <span>30-day free returns on eligible orders</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
